import { useState } from "react";
import { useOnboarding } from "@/hooks/useOnboarding";
import { HelpCircle, Zap, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

const tabs = [
  {
    id: "how",
    label: "How to analyze",
    icon: HelpCircle,
    content: (
      <ul className="space-y-1.5 text-xs text-muted-foreground leading-relaxed">
        <li className="flex items-start gap-2">
          <span className="text-cyan/60 mt-0.5">1.</span>
          Paste the toxic or manipulative message
        </li>
        <li className="flex items-start gap-2">
          <span className="text-cyan/60 mt-0.5">2.</span>
          Choose context — Professional, Personal, or Public
        </li>
        <li className="flex items-start gap-2">
          <span className="text-cyan/60 mt-0.5">3.</span>
          Hit Neutralize to run the analysis
        </li>
        <li className="flex items-start gap-2">
          <span className="text-cyan/60 mt-0.5">4.</span>
          Review your score, detected pattern, and strategy
        </li>
      </ul>
    ),
  },
  {
    id: "credits",
    label: "Credits",
    icon: Zap,
    content: (
      <ul className="space-y-1.5 text-xs text-muted-foreground leading-relaxed">
        <li>• Each analysis uses <span className="text-foreground/80 font-medium">1 credit</span></li>
        <li>• Generating a response strategy uses <span className="text-foreground/80 font-medium">1 credit</span></li>
        <li>• Browsing Feed and Insights is <span className="text-foreground/80 font-medium">free</span></li>
        <li>• Your credit balance is shown in the top bar</li>
      </ul>
    ),
  },
  {
    id: "after",
    label: "After analysis",
    icon: ArrowRight,
    content: (
      <ul className="space-y-1.5 text-xs text-muted-foreground leading-relaxed">
        <li>• Review your toxicity score and manipulation pattern</li>
        <li>• Go to <span className="text-foreground/80 font-medium">Strategy</span> to draft a neutralized response</li>
        <li>• Go to <span className="text-foreground/80 font-medium">Content</span> to create shareable assets</li>
        <li>• Results auto-save to <span className="text-foreground/80 font-medium">Reports</span></li>
      </ul>
    ),
  },
] as const;

const AnalyzeGettingStarted = () => {
  const { isNewUser } = useOnboarding();
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const handleTabClick = (id: string) => {
    setActiveTab((prev) => (prev === id ? null : id));
  };

  const activeContent = tabs.find((t) => t.id === activeTab);

  return (
    <div className="mb-4 space-y-0">
      <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase mb-1.5 block">
        Getting Started
      </span>

      {/* Horizontal tab row */}
      <div className="flex gap-1.5">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={cn(
                "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200",
                "border",
                isActive
                  ? "border-cyan/30 bg-cyan/10 text-cyan"
                  : "border-border/40 bg-card/30 text-muted-foreground hover:text-foreground hover:border-border/60"
              )}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Shared content panel */}
      {activeContent && (
        <div className="mt-1.5 px-4 py-3 rounded-lg border border-cyan/15 bg-card/30 animate-in fade-in slide-in-from-top-1 duration-200">
          {activeContent.content}
        </div>
      )}
    </div>
  );
};

export default AnalyzeGettingStarted;
